import { secureApi } from './secureApi';

// Guest relocation — where a booking's guest PHYSICALLY is.
// Plan: tasks/guest-relocation-v1.md · Decisions: docs/03_Technical/GUEST_RELOCATION_DESIGN.md
//
// The booking names the unit that was SOLD and never changes. After a move the guest is somewhere
// else, and until the operational surfaces are migrated (stage 2) the screens still resolve the
// unit from the booking — so this is what keeps a human from acting on the wrong apartment.
//
// Its own endpoint rather than a field on the booking payload, matching guestArrivalApi and for the
// same reason: the booking page, the arrivals view and the inbox all show this one fact, and
// fetching it three different ways is three chances for them to disagree about where a guest is
// sleeping.
//
// ⚠️ `relocation: null` means "the guest is in the unit they booked" — a real answer, not a
// failure to look. Never render it as unknown.
const relocationApi = {
  // GET /api/v1/relocations/current/:bookingId
  // → { success, relocation: { bookedUnit, currentUnit, from, to,
  //                            oldCodeStatus } | null }
  //
  // The id is in the PATH, not a query param — matching the two other booking-scoped read services
  // (guestArrivalApi, departureApi). One shape for "a fact about this booking".
  getCurrent: async (bookingId) => {
    const response = await secureApi.get(`/api/v1/relocations/current/${bookingId}`);
    return response.data;
  },

  // The same fact for a whole LIST, in one round trip.
  // → { success, relocations: { [bookingId]: relocation | null } }
  //
  // ⚠️ Every id asked for comes back as a key. A missing key and a null value read identically to
  // `map[id]`, and null is a real answer — "this guest is in the unit they booked" — so a list
  // surface must be able to trust it rather than guess.
  //
  // Exists because the arrivals view and the inbox render ~30 rows: one call per row is 30 requests
  // for a fact that is null on almost all of them, and a list that fires 30 calls is a list somebody
  // eventually "fixes" by dropping the banner.
  getCurrentBatch: async (bookingIds) => {
    const ids = [...new Set((bookingIds || []).map(String).filter(Boolean))];
    if (!ids.length) return { success: true, relocations: {} };
    const response = await secureApi.get('/api/v1/relocations/current', {
      params: { bookingIds: ids.join(',') },
      preserveBackendError: true
    });
    return response.data;
  },

  // Which units this guest could be moved into, and which were ruled out and why.
  // → { success, from, to, candidates: [...], ineligible: [{ propertyId, name, reason }], notEligible }
  //
  // `preserveBackendError` for the same reason as `create`: without it the shared interceptor
  // rewrites the body and the caller's `.data.error` read comes back undefined, so a permission
  // refusal renders as the generic "could not load units".
  getCandidates: async (bookingId) => {
    const response = await secureApi.get(`/api/v1/relocations/candidates/${bookingId}`, { preserveBackendError: true });
    return response.data;
  },

  // Record the move. `preserveBackendError` because every refusal here is one the operator needs
  // verbatim — "another guest is in that unit" and "that unit is sold" lead somewhere different.
  create: async (payload) => {
    const response = await secureApi.post('/api/v1/relocations', payload, { preserveBackendError: true });
    return response.data;
  },

  // Every move on a booking, newest first — the step-by-step panel reads this.
  list: async (bookingId) => {
    const response = await secureApi.get('/api/v1/relocations', { params: { bookingId }, preserveBackendError: true });
    return response.data;
  },

  advance: async (id, state) => {
    const response = await secureApi.patch(`/api/v1/relocations/${id}/state`, { state }, { preserveBackendError: true });
    return response.data;
  },

  cancel: async (id, cancelReason) => {
    const response = await secureApi.post(`/api/v1/relocations/${id}/cancel`, { cancelReason }, { preserveBackendError: true });
    return response.data;
  }
};

export default relocationApi;
